<template>
  <nav class="relative z-40 bg-white dark:bg-slate-900 shadow-md border-b border-slate-200 dark:border-slate-700">
    <div class="container mx-auto p-4 flex justify-between items-center">
      <!-- Logo -->
      <router-link to="/dashboard" class="text-slate-900 dark:text-white font-bold text-xl hover:opacity-80 transition-opacity cursor-pointer">
        BetterConnect
      </router-link>

      <!-- Hamburger Menu for Mobile -->
      <button @click="toggleSidebar" class="text-slate-900 dark:text-white hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none md:hidden transition-colors">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2.5"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <!-- Navigation Links (Desktop) -->
      <ul class="hidden md:flex space-x-1 items-center">
        <li>
          <router-link
              to="/dashboard"
              class="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
          >
            Home
          </router-link>
        </li>
        <li>
          <router-link
              to="/assignments"
              class="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
          >
            Assignments
          </router-link>
        </li>
        <li>
          <router-link
              to="/schedule"
              class="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
          >
            Schedule
          </router-link>
        </li>
        <li>
          <router-link
              to="/resources"
              class="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
          >
            Resources
          </router-link>
        </li>

        <li>
          <router-link
              to="/menu"
              class="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
          >
            Menu
          </router-link>
        </li>

        <li v-if="adminRoles?.isAdmin">
          <router-link
              to="/admin"
              class="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
          >
            Admin
          </router-link>
        </li>

        <!-- Classes Dropdown -->
        <li
            v-if="isAuthenticated && classes.length"
            class="relative"
            @mouseenter="showClassesDropdown = true"
            @mouseleave="showClassesDropdown = false"
        >
          <div class="px-4 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white cursor-pointer flex items-center transition-all duration-200 font-medium">
            <span>Classes</span>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 ml-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <!-- Dropdown Menu -->
          <transition name="fade">
            <ul
                v-show="showClassesDropdown"
                class="absolute left-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-2"
            >
              <li v-for="classItem in classes" :key="classItem.section_id">
                <router-link
                    :to="`/class/${classItem.section_id}`"
                    class="block rounded-lg px-2 py-2.5 text-slate-700 dark:text-slate-300 hover:bg-slate-100/70 dark:hover:bg-slate-700/50 transition-colors duration-200 font-medium"
                >
                  {{ classItem.section_display_name }}
                </router-link>
              </li>
            </ul>
          </transition>
        </li>
      </ul>

      <!-- User Section (Desktop) -->
      <div class="hidden md:flex items-center space-x-3">
        <!-- Theme Toggle Button -->
        <button
            @click="toggleTheme"
            class="p-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none transition-all duration-200"
        >
          <span v-if="theme === 'light'">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
            </svg>
          </span>
          <span v-else>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m8-8h1M3 12H2m15.364-7.364l.707.707M5.636 18.364l-.707.707m12.728 0l-.707-.707M5.636 5.636l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </span>
        </button>

        <!-- If Authenticated, Show Profile -->
        <div v-if="isAuthenticated" class="relative" ref="userMenu">
          <div @click="toggleUserMenu" class="flex items-center space-x-2 cursor-pointer px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200">
            <img
                :src="profilePhotoUrl || '/default-profile.png'"
                alt="User Profile"
                class="w-12 h-12 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700"
            />
            <span class="text-slate-900 dark:text-white font-medium hidden lg:block">
              {{ firstName }} {{ lastName }}
            </span>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-slate-700 dark:text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          <!-- Dropdown Menu -->
          <transition name="fade">
            <div
                v-if="showUserMenu"
                class="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-2 z-20"
            >
              <ul>
                <li>
                  <button
                      @click="openSettings"
                      class="w-full text-left px-4 py-2.5 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors font-medium"
                  >
                    Settings
                  </button>
                </li>
                <li>
                  <button
                      @click="logout"
                      class="w-full text-left px-4 py-2.5 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors font-medium"
                  >
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </transition>
        </div>

        <!-- If Not Authenticated, Show Login Button -->
        <div v-else>
          <button
              @click="redirectToLogin"
              class="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          >
            Login
          </button>
        </div>
      </div>
    </div>

    <!-- Sidebar (Mobile) -->
    <transition name="slide">
      <div v-if="isSidebarOpen" class="fixed inset-0 flex z-50">
        <!-- Overlay -->
        <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeSidebar"></div>
        <!-- Sidebar Content -->
        <div class="relative w-72 bg-white dark:bg-slate-900 z-50 shadow-2xl">
          <div class="p-6">
            <!-- Close Button -->
            <button
                @click="closeSidebar"
                class="absolute top-6 right-6 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <!-- Logo -->
            <router-link @click.native="closeSidebar" to="/dashboard" class="text-slate-900 dark:text-white font-bold text-xl mb-8 hover:opacity-80 transition-opacity cursor-pointer block">
              BetterConnect
            </router-link>

            <!-- Navigation Links -->
            <ul class="space-y-2">
              <li>
                <router-link
                    @click.native="closeSidebar"
                    to="/dashboard"
                    class="block px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
                >
                  Home
                </router-link>
              </li>
              <li>
                <router-link
                    @click.native="closeSidebar"
                    to="/assignments"
                    class="block px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
                >
                  Assignments
                </router-link>
              </li>
              <li>
                <router-link
                    @click.native="closeSidebar"
                    to="/schedule"
                    class="block px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
                >
                  Schedule
                </router-link>
              </li>
              <li>
                <router-link
                    @click.native="closeSidebar"
                    to="/resources"
                    class="block px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
                >
                  Resources
                </router-link>
              </li>
              <li v-if="adminRoles?.isAdmin">
                <router-link
                    @click.native="closeSidebar"
                    to="/admin"
                    class="block px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200 font-medium"
                >
                  Admin
                </router-link>
              </li>

              <!-- Classes Dropdown -->
              <li v-if="isAuthenticated && classes.length">
                <div
                    @click="toggleMobileClassesDropdown"
                    class="flex items-center justify-between px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-all duration-200 font-medium"
                >
                  <span>Classes</span>
                  <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 transition-transform duration-200"
                      :class="{ 'transform rotate-180': showMobileClassesDropdown }"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <transition name="fade">
                  <ul v-show="showMobileClassesDropdown" class="mt-2 ml-4 space-y-1">
                    <li v-for="classItem in classes" :key="classItem.section_id">
                      <router-link
                          @click.native="closeSidebar"
                          :to="`/class/${classItem.section_id}`"
                          class="block px-4 py-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all duration-200"
                      >
                        {{ classItem.section_display_name }}
                      </router-link>
                    </li>
                  </ul>
                </transition>
              </li>
            </ul>

            <!-- User Section -->
            <div class="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
              <!-- If Authenticated -->
              <div v-if="isAuthenticated">
                <div @click="toggleMobileUserMenu" class="flex items-center space-x-3 cursor-pointer px-4 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200">
                  <img
                      :src="profilePhotoUrl || '/default-profile.png'"
                      alt="User Profile"
                      class="w-10 h-10 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700"
                  />
                  <div class="flex-1">
                    <span class="block text-slate-900 dark:text-white font-semibold">
                      {{ firstName }} {{ lastName }}
                    </span>
                  </div>
                  <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 text-slate-700 dark:text-slate-300 transition-transform duration-200"
                      :class="{ 'transform rotate-180': showMobileUserMenu }"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                <transition name="fade">
                  <div v-show="showMobileUserMenu" class="mt-2 space-y-1">
                    <button
                        @click="handleSettings()"
                        class="w-full text-left px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 font-medium"
                    >
                      Settings
                    </button>
                    <button
                        @click="handleLogout()"
                        class="w-full text-left px-4 py-2.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 font-medium"
                    >
                      Logout
                    </button>
                  </div>
                </transition>
              </div>

              <!-- If Not Authenticated -->
              <div v-else>
                <button
                    @click="redirectToLogin"
                    class="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Settings Modal -->
    <transition name="fade">
      <div v-if="showSettingsModal" class="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4">
        <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 max-w-sm w-full max-h-[85vh] overflow-y-auto border border-slate-200 dark:border-slate-700">
          <h2 class="text-xl font-bold mb-5 text-slate-900 dark:text-white">Settings</h2>

          <!-- DIGEST TOGGLE -->
          <div class="mb-4">
            <label class="flex items-center text-slate-700 dark:text-slate-300 cursor-pointer">
              <input type="checkbox" v-model="digestEnabled" class="form-checkbox mr-3" />
              <span class="font-medium">Enable Digest</span>
            </label>
          </div>

          <!-- DIGEST TIMINGS -->
          <div v-if="digestEnabled" class="mb-4">
            <label class="block text-slate-700 dark:text-slate-300 mb-2 font-medium">Digest Timings</label>
            <div class="space-y-1.5">
              <label class="flex items-center text-slate-700 dark:text-slate-300 cursor-pointer">
                <input type="checkbox" value="everyMorning" v-model="digestTimings" class="form-checkbox mr-3" />
                <span>Every Morning</span>
              </label>
              <label class="flex items-center text-slate-700 dark:text-slate-300 cursor-pointer">
                <input type="checkbox" value="everyNight" v-model="digestTimings" class="form-checkbox mr-3" />
                <span>Every Night</span>
              </label>
            </div>
            <p v-if="digestTimingError" class="text-red-500 text-sm mt-2">{{ digestTimingError }}</p>
          </div>

          <!-- PHONE + SMS OPT-IN -->
          <div v-if="digestEnabled" class="mb-4">
            <div class="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 p-3 space-y-3">
              <div class="flex items-center justify-between gap-3">
                <p class="font-semibold text-slate-900 dark:text-white">Text Digests</p>
                <span
                    class="shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                    :class="hasRecordedConsent ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'"
                >
                  {{ hasRecordedConsent ? 'Consent saved' : 'Consent required' }}
                </span>
              </div>

              <div>
                <label class="block text-slate-700 dark:text-slate-300 mb-2 font-medium">Mobile Number</label>
                <input
                    type="tel"
                    v-model="phoneNumber"
                    class="form-input w-full"
                    placeholder="+1xxxxxxxxxx"
                    inputmode="tel"
                    autocomplete="tel"
                />
                <p v-if="phoneNumberError" class="text-red-500 text-sm mt-1">{{ phoneNumberError }}</p>
              </div>

              <div v-if="hasRecordedConsent" class="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-3">
                <p class="text-xs leading-5 text-emerald-700 dark:text-emerald-400">
                  SMS consent saved for {{ maskedPhone }} {{ lastConsentAtLabel }}. Reply STOP to opt out or HELP for help.
                </p>
              </div>

              <template v-else>
                <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-3">
                  <p class="text-xs leading-5 text-slate-600 dark:text-slate-400">
                    By checking the box and saving, you agree to receive recurring BetterConnect Alerts assignment digest texts at this number. Up to 2 autodialed messages per day. Msg & data rates may apply. Consent is not required to use BetterConnect. Reply STOP to opt out or HELP for help.
                  </p>

                  <label class="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3 cursor-pointer">
                    <input type="checkbox" v-model="consentCheckbox" class="form-checkbox form-checkbox-lg mt-0.5" />
                    <span class="text-sm leading-5 text-slate-700 dark:text-slate-300">
                      I agree to receive text digests at this number and accept the
                      <a href="/terms" class="text-indigo-600 dark:text-indigo-400 hover:underline">Terms</a>
                      and
                      <a href="/privacy" class="text-indigo-600 dark:text-indigo-400 hover:underline">Privacy Policy</a>.
                    </span>
                  </label>
                </div>
              </template>

              <p v-if="consentInfo" class="text-emerald-600 dark:text-emerald-400 text-sm">{{ consentInfo }}</p>
              <p v-if="consentError" class="text-red-500 text-sm">{{ consentError }}</p>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-5">
            <button
                @click="closeSettings"
                class="px-5 py-2.5 rounded-xl font-semibold bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-all duration-200"
            >
              Cancel
            </button>
            <button
                @click="saveSettings"
                class="px-5 py-2.5 rounded-xl font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { inject } from 'vue';
import { clearAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';

export default {
  data() {
    return {
      isAuthenticated: !!localStorage.getItem('token'),
      isSidebarOpen: false,
      showMobileUserMenu: false,
      showMobileClassesDropdown: false,
      showUserMenu: false,
      phoneNumber: '',
      profilePhotoUrl: '',
      firstName: '',
      lastName: '',
      showSettingsModal: false,
      digestEnabled: false,
      digestTimings: [],
      deliveryMethod: {
        text: true,
      },
      phoneNumberError: '',
      digestTimingError: '',
      authCheckInterval: null,
      classes: [],
      showClassesDropdown: false,
      consentStatus: 'unverified',
      consentCheckbox: false,
      consentError: '',
      consentInfo: '',
      lastConsentAt: null,
      consentedPhoneNumber: '',
    };
  },
  setup() {
    const theme = inject('theme');
    const setTheme = inject('setTheme');
    const adminRoles = inject('adminRoles', null);

    const toggleTheme = () => {
      const newTheme = theme.value === 'light' ? 'dark' : 'light';
      setTheme(newTheme);
    };

    return {
      theme,
      toggleTheme,
      adminRoles,
    };
  },
  computed: {
    phoneIsValid() {
      return /^\+1\d{10}$/.test(this.phoneNumber);
    },
    hasRecordedConsent() {
      return (
          this.phoneIsValid &&
          this.phoneNumber === this.consentedPhoneNumber &&
          (Boolean(this.lastConsentAt) || this.isConsentedStatus(this.consentStatus))
      );
    },
    maskedPhone() {
      if (!this.phoneIsValid) return '';
      return `+1******${this.phoneNumber.slice(-4)}`;
    },
    lastConsentAtLabel() {
      if (!this.lastConsentAt) return '';
      try {
        const d = new Date(this.lastConsentAt);
        return `on ${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
      } catch (_) {
        return '';
      }
    },
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = true;
    },
    closeSidebar() {
      this.isSidebarOpen = false;
      this.showMobileUserMenu = false;
      this.showMobileClassesDropdown = false;
    },
    toggleMobileUserMenu() {
      this.showMobileUserMenu = !this.showMobileUserMenu;
    },
    toggleMobileClassesDropdown() {
      this.showMobileClassesDropdown = !this.showMobileClassesDropdown;
    },
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu;
    },
    redirectToLogin() {
      beginLogin({ originalUrl: window.location.href });
    },
    logout() {
      clearAuthToken();
      this.isAuthenticated = false;
      this.profilePhotoUrl = '';
      this.firstName = '';
      this.lastName = '';
      this.classes = [];
      this.$router.push('/');
    },
    async fetchProfilePhoto() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(backendUrl('/profile_photo'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile photo');
        }

        const data = await response.json();
        this.profilePhotoUrl = data.profile_photo;
      } catch (error) {
        console.error('Error fetching profile photo:', error);
        this.profilePhotoUrl = '/default-profile.png';
      }
    },
    async fetchUserInfo() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(backendUrl('/user_info'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }

        const data = await response.json();
        this.firstName = data.firstName;
        this.lastName = data.lastName;
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    },
    openSettings() {
      this.showUserMenu = false;
      this.showSettingsModal = true;
      this.consentCheckbox = false;
      this.consentError = '';
      this.consentInfo = '';
      this.loadSettings();
    },
    closeSettings() {
      this.showSettingsModal = false;
      this.resetErrors();
      this.consentError = '';
      this.consentInfo = '';
      this.consentCheckbox = false;
    },
    isConsentedStatus(status) {
      return ['verified', 'recorded', 'consented', 'opted_in'].includes(status);
    },
    formatPhoneNumber() {
      let number = this.phoneNumber || '';
      number = number.replace(/\D/g, '');
      if (!number) {
        this.phoneNumber = '';
        return;
      }
      if (!number.startsWith('1')) {
        number = '1' + number;
      }
      number = '+' + number.slice(0, 11);
      this.phoneNumber = number;
    },
    async saveSettings() {
      this.phoneNumberError = '';
      this.digestTimingError = '';
      this.consentError = '';
      this.consentInfo = '';

      let isValid = true;

      if (this.digestEnabled) {
        this.deliveryMethod = { text: true };

        if (this.digestTimings.length === 0) {
          this.digestTimingError = 'Please select at least one digest timing.';
          isValid = false;
        }

        if (this.deliveryMethod.text) {
          const phoneRegex = /^\+1\d{10}$/;
          if (!this.phoneNumber) {
            this.phoneNumberError = 'Phone number is required when Text delivery method is selected.';
            isValid = false;
          } else if (!phoneRegex.test(this.phoneNumber)) {
            this.phoneNumberError = 'Phone number must be in the format +1xxxxxxxxxx';
            isValid = false;
          } else if (!this.hasRecordedConsent && !this.consentCheckbox) {
            this.consentError = 'Please agree to the SMS opt-in terms before enabling text digests.';
            isValid = false;
          }
        }
      }

      if (!isValid) return;

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const shouldRecordConsent = this.digestEnabled && this.deliveryMethod.text && !this.hasRecordedConsent && this.consentCheckbox;
        const consentStatus = shouldRecordConsent ? 'recorded' : this.consentStatus;
        const lastConsentAt = shouldRecordConsent ? new Date().toISOString() : this.lastConsentAt;

        const settings = {
          digestEnabled: this.digestEnabled,
          digestTimings: this.digestTimings,
          deliveryMethod: { text: true },
          phoneNumber: this.deliveryMethod.text ? this.phoneNumber : null,
        };

        if (shouldRecordConsent || this.hasRecordedConsent) {
          settings.consentStatus = consentStatus;
          settings.lastConsentAt = lastConsentAt;
        }

        const response = await fetch(backendUrl('/user_settings'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(settings),
        });

        if (!response.ok) {
          throw new Error('Failed to save settings.');
        }

        if (shouldRecordConsent) {
          this.consentStatus = consentStatus;
          this.lastConsentAt = lastConsentAt;
          this.consentedPhoneNumber = this.phoneNumber;
        }

        this.consentCheckbox = false;
        this.closeSettings();
      } catch (error) {
        console.error('Error saving settings:', error);
        this.consentError = 'Could not save digest settings right now. Please try again.';
      }
    },
    async loadSettings() {
      const token = localStorage.getItem('token');
      if (!token) return;

      this.digestEnabled = false;
      this.digestTimings = [];
      this.deliveryMethod = { text: true };
      this.phoneNumber = '';
      this.consentStatus = 'unverified';
      this.lastConsentAt = null;
      this.consentedPhoneNumber = '';

      try {
        const response = await fetch(backendUrl('/user_settings'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 404) {
          // No settings yet
        } else if (!response.ok) {
          throw new Error('Failed to load settings.');
        } else {
          const settings = await response.json();
          this.digestEnabled = !!settings.digestEnabled;
          this.digestTimings = Array.isArray(settings.digestTimings) ? settings.digestTimings : [];
          this.deliveryMethod = { text: true };
          this.phoneNumber = settings.phoneNumber || '';
          if (this.phoneNumber) this.formatPhoneNumber();
          if (settings.consentStatus) this.consentStatus = settings.consentStatus;
          if (settings.lastConsentAt) this.lastConsentAt = settings.lastConsentAt;
          if ((settings.lastConsentAt || this.isConsentedStatus(settings.consentStatus)) && this.phoneIsValid) {
            this.consentedPhoneNumber = this.phoneNumber;
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    },
    handleClickOutside(event) {
      const userMenu = this.$refs.userMenu;
      if (userMenu && !userMenu.contains(event.target)) {
        this.showUserMenu = false;
      }
    },
    checkAuthStatus() {
      const tokenExists = !!localStorage.getItem('token');
      if (tokenExists && !this.isAuthenticated) {
        this.isAuthenticated = true;
        this.fetchProfilePhoto();
        this.fetchUserInfo();
        this.loadSettings();
        this.fetchClasses();
      } else if (!tokenExists && this.isAuthenticated) {
        this.isAuthenticated = false;
        this.profilePhotoUrl = '';
        this.firstName = '';
        this.lastName = '';
        this.showUserMenu = false;
        this.showSettingsModal = false;
        this.classes = [];
      }
    },
    resetErrors() {
      this.phoneNumberError = '';
      this.digestTimingError = '';
    },
    async fetchClasses() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(backendUrl('/classes'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch classes');
        }

        const data = await response.json();
        this.classes = data.value || [];
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    },
    handleSettings() {
      this.openSettings();
      this.closeSidebar();
    },
    handleLogout() {
      this.logout();
      this.closeSidebar();
    },
  },
  created() {
    if (this.isAuthenticated) {
      this.fetchProfilePhoto();
      this.fetchUserInfo();
      this.loadSettings();
      this.fetchClasses();
    }
  },
  mounted() {
    this.authCheckInterval = setInterval(this.checkAuthStatus, 1000);
  },
  watch: {
    showUserMenu(newVal) {
      if (newVal) {
        document.addEventListener('click', this.handleClickOutside);
      } else {
        document.removeEventListener('click', this.handleClickOutside);
      }
    },
    phoneNumber(newValue, oldValue) {
      this.formatPhoneNumber();
      const phoneChanged = this.phoneNumber !== this.consentedPhoneNumber;
      if (!phoneChanged) return;

      if (this.lastConsentAt || this.isConsentedStatus(this.consentStatus)) {
        this.consentStatus = 'unverified';
        this.lastConsentAt = null;
        this.consentedPhoneNumber = '';
        this.consentCheckbox = false;
        this.consentError = '';
        this.consentInfo = oldValue ? 'Number changed. Review the opt-in and save again.' : '';
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.authCheckInterval) {
      clearInterval(this.authCheckInterval);
    }
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.form-input {
  background-color: #f9fafb;
  color: #1f2937;
  border: 1px solid #d1d5db;
  padding: 0.625rem;
  border-radius: 0.5rem;
}

.dark .form-input {
  background-color: #374151;
  color: #f9fafb;
  border: 1px solid #4b5563;
}

.form-input::placeholder {
  color: #9ca3af;
}

.dark .form-input::placeholder {
  color: #6b7280;
}

.form-checkbox {
  appearance: none;
  -webkit-appearance: none;
  background-color: transparent;
  border: 2px solid #cbd5e0;
  border-radius: 0.25rem;
  width: 1.125rem;
  height: 1.125rem;
  position: relative;
  cursor: pointer;
  transition: all 0.2s;
}

.form-checkbox:checked {
  background-color: #4f46e5;
  border-color: #4f46e5;
}

.form-checkbox:checked::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.375rem;
  height: 0.625rem;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: translate(-50%, -60%) rotate(45deg);
}

.form-checkbox-lg {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 0.35rem;
  flex-shrink: 0;
}

.form-checkbox-lg:checked::after {
  width: 0.45rem;
  height: 0.78rem;
  border-width: 0 2.5px 2.5px 0;
}

.dark .form-checkbox {
  border-color: #6b7280;
}

.dark .form-checkbox:checked {
  background-color: #6366f1;
  border-color: #6366f1;
}
</style>
